import React, { useState, useEffect } from "react";
import { Button, ButtonGroup, Col, Input, InputGroup, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { Triangle } from "react-loader-spinner";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AddMenuItem from "../Component/AddMenuItem";
import ItemService from "../../../Services/QRCodeServices/MenuItem/ItemService";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";
import EditMenuItem from "../Component/EditMenuItem";
import CategoryService from "../../../Services/QRCodeServices/MenuCategory/CategoryService";
import PrintHeader from "../../../Components/PrintHeader/PrintHeader";
import PrintableTable from "../Component/PrintableTable";
import NumberCommas from "../../../Components/NumberCommas/NumberCommas";
import Select from "react-select";

const MenuItem = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]); // New state for all items
  const [categoryData, setCategoryData] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [isPrinting, setIsPrinting] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalData: 0,
  });
  const [selectedCategory, setSelectedCategory] = useState(null);

  const toggleModal = () => setModalOpen(!modalOpen);

  const fetchData = async (
    pageNumber = 1,
    pageSize = 10,
    fetchAll = false,
    searchText = "",
    categoryId = null
  ) => {
    setLoading(true);
    try {
      const response = await ItemService.getList({
        pageNumber: fetchAll ? 1 : pageNumber,
        pageSize: fetchAll ? 9999 : pageSize,
        itemName: searchText || null, // send search text
        categoryId: categoryId || null, // send categoryId
      });

      if (response && response.data) {
        if (fetchAll) {
          setAllData(response.data);
        } else {
          setData(response.data);
          setPagination({
            currentPage: response.currentPage,
            pageSize: response.pageSize,
            totalPages: response.totalPages,
            totalData: response.totalData,
          });
        }
      } else {
        setData([]);
        if (fetchAll) setAllData([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      if (fetchAll) setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  // Watch filters
  useEffect(() => {
    fetchData(
      1,
      pagination.pageSize,
      false,
      filterText,
      selectedCategory?.value
    );
  }, [filterText, selectedCategory]);

  const fetchCategoryData = async () => {
    try {
      const response = await CategoryService.getList();
      console.log("🚀 ~ fetchCategoryData ~ response:", response);
      setCategoryData(response);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchCategoryData();
  }, []);

  // Delete functionality
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await ItemService.delete(selectedDeleteItem.id);
        toast.success("Selected Package deleted successfully");
        fetchData(pagination.currentPage, pagination.pageSize);
      } catch (error) {
        toast.error("Failed to delete the record");
      } finally {
        toggleDeleteModal();
        setSelectedDeleteItem(null);
      }
    }
  };

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDeleteModal();
  };

  // Edit functionality
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedEditItem, setSelectedEditItem] = useState(null);
  const toggleEditModal = () => setEditModalOpen(!editModalOpen);

  const handleEditClick = (item) => {
    setSelectedEditItem(item);
    toggleEditModal();
  };

  // Handle page change
  const handlePageChange = (page) => {
    fetchData(
      page,
      pagination.pageSize,
      false,
      filterText,
      selectedCategory?.value
    );
  };

  const handlePerRowsChange = async (newPageSize, page) => {
    fetchData(page, newPageSize, false, filterText, selectedCategory?.value);
  };

  const filteredData = Array.isArray(data)
    ? data.filter((item) => {
        const searchText = filterText.toLowerCase();
        return (
          item?.title?.toLowerCase().includes(searchText) ||
          item?.ingredients?.toLowerCase().includes(searchText)
        );
      })
    : [];

  const filteredAllData = Array.isArray(allData)
    ? allData.filter((item) => {
        const searchText = filterText.toLowerCase();
        return (
          item?.title?.toLowerCase().includes(searchText) ||
          item?.ingredients?.toLowerCase().includes(searchText)
        );
      })
    : [];

  const handlePrint = () => {
    setIsPrinting(true);
    fetchData(1, 10, true).then(() => {
      // Fetch all data for printing
      setTimeout(() => {
        window.print();
        setIsPrinting(false);
      }, 500);
    });
  };

  const columns = [
    {
      name: "S.N",
      selector: (row) => row?.index,
      cell: (row, index) =>
        (pagination.currentPage - 1) * pagination.pageSize + index + 1,
      sortable: true,
      width: "75px",
    },
    {
      name: "Image",
      selector: (row) =>
        row?.cloudR2ImagePath ? (
          <img
            src={row?.cloudR2ImagePath?.getCDNUrl()}
            alt={row?.title || "Image"}
            style={{ height: "50px", width: "50px", objectFit: "cover" }}
          />
        ) : null,
      sortable: false,
    },
    {
      name: "Title",
      selector: (row) => row?.title,
      cell: (row) => (
        <Link to={`/menu-item/detail/${row?.id}`}>{row?.title}</Link>
      ),
      sortable: true,
    },
    // {
    //   name: "Unit",
    //   selector: (row) => row.unit || "N/A",
    //   sortable: true,
    // },
    {
      name: "Category",
      selector: (row) => row.menuCategory,
      sortable: true,
    },
    {
      name: "VAT Amt",
      selector: (row) => row.vatAmount,
      sortable: true,
      right: true,
    },
    {
      name: "Item Amt",
      selector: (row) => row.itemAmount,
      sortable: true,
      right: true,
    },
    {
      name: "Price",
      selector: (row) => row?.price ?? "N/A",
      cell: (row) => <NumberCommas number={row?.price} />,
      sortable: true,
      right: true,
    },

    {
      name: "Actions",
      cell: (row) => (
        <ButtonGroup className="gap-3">
          <Button
            color="warning"
            className="rounded-circle btn-sm d-flex align-items-center justify-content-center"
            style={{ width: "30px", height: "30px" }}
            onClick={() => handleEditClick(row)}
          >
            <i className="bx bx-edit"></i>
          </Button>
          <Button
            color="danger"
            className="rounded-circle btn-sm d-flex align-items-center justify-content-center"
            style={{ width: "30px", height: "30px" }}
            onClick={() => handleDeleteClick(row)}
          >
            <i className="bx bx-trash"></i>
          </Button>
        </ButtonGroup>
      ),
      center: true,
    },
  ];

  const categoryOption = categoryData?.map((item) => ({
    value: item.id, // or item._id if using MongoDB
    label: item.name, // or whichever field contains category name
  }));
  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="Menu Item List" pageTitle="Menu Item" />
      </div>
      <PrintHeader reportTitle="Menu Item List" />
      <Row className="d-print-none">
        <Col md={5} xs={6} className="px-xs-1">
          <InputGroup>
            <Input
              placeholder="Search by title"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={3}>
          <Select
            isClearable
            className="z-3"
            options={categoryOption}
            value={selectedCategory}
            onChange={(selectedOption) => setSelectedCategory(selectedOption)}
            placeholder="Filter by Category"
          />
        </Col>

        <Col
          md={4}
          xs={6}
          className="d-flex justify-content-end align-items-end mb-3"
        >
          <Button
            className="package-button px-1 px-sm-2 me-2"
            onClick={toggleModal}
          >
            Add New Item
          </Button>
          <Button
            color="outline-secondary"
            className="py-2 px-3 d-flex align-items-center shadow-sm"
            onClick={handlePrint}
          >
            <i className="ri-printer-line fs-15"></i>
          </Button>
        </Col>
      </Row>
      {isPrinting ? (
        <PrintableTable filteredData={filteredAllData} />
      ) : (
        <CustomDataTable
          responsive
          striped
          pagination
          paginationServer
          paginationTotalRows={pagination.totalData}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          fixedHeader
          persistTableHead
          progressPending={loading}
          columns={columns}
          data={filteredData}
          customStyles={{
            headCells: {
              style: {
                fontSize: "0.93rem",
                fontWeight: 610,
              },
            },
            rows: {
              style: {
                fontSize: "0.9rem",
              },
            },
          }}
          progressComponent={
            <div className="my-3">
              <Triangle
                visible={true}
                height="80"
                width="80"
                color="#5B71B9"
                ariaLabel="triangle-loading"
                wrapperStyle={{}}
                wrapperClass=""
              />
              <h5 className="mt-1">Loading...</h5>
            </div>
          }
        />
      )}

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={toggleDeleteModal}
        selectedItem={selectedDeleteItem}
      />
      <AddMenuItem
        isOpen={modalOpen}
        toggleModal={toggleModal}
        fetchCategoryData={fetchData}
      />
      <EditMenuItem
        isOpen={editModalOpen}
        toggleModal={toggleEditModal}
        data={selectedEditItem}
        refreshData={fetchData}
        allData={categoryData}
        fetchCategoryData={fetchData}
      />
    </div>
  );
};

export default MenuItem;
